import type { ReactNode } from 'react';
import { useChatContext } from '@/chat/ChatContext';
import { BookIcon, FileIcon } from '@/components/icons';

export interface PageLinkProps {
  slug: string;
}

export type LinkKind = 'blog' | 'page' | 'generic';

/**
 * Classify a URL by its path so callers can pick the right tint + glyph
 * for inline references.
 */
function classifyLinkUrl(url: string | undefined | null): LinkKind {
  if (!url) return 'generic';
  if (/\/blog\//i.test(url) || /\/blogs\//i.test(url)) return 'blog';
  if (/\/pages?\//i.test(url)) return 'page';
  return 'generic';
}

const LINK_KIND_CLASSES: Record<LinkKind, string> = {
  // Blog: green accent (matches brand) + book icon
  blog: 'tw-text-cal-green-700 tw-decoration-cal-green-200 hover:tw-decoration-cal-green-500 hover:tw-text-cal-green-900',
  // Page: purple accent + file icon — visually distinct from blog/product
  page: 'tw-text-purple-700 tw-decoration-purple-200 hover:tw-decoration-purple-500 hover:tw-text-purple-900',
  // Fallback (unknown URL shape)
  generic:
    'tw-text-cal-ink-soft tw-decoration-cal-border hover:tw-decoration-cal-ink-soft hover:tw-text-cal-ink',
};

/** Render the leading glyph for a link-kind, or null for the generic kind. */
function LinkKindGlyph({ kind }: { kind: LinkKind }): ReactNode {
  if (kind === 'blog') return <BookIcon width={12} height={12} />;
  if (kind === 'page') return <FileIcon width={12} height={12} />;
  return null;
}

export function PageLink({ slug }: PageLinkProps): JSX.Element | null {
  const { payload } = useChatContext();
  const page = payload.pages.find((p) => p.slug === slug);

  // The provider payload is the source of truth for renderable links. Hiding
  // unresolved slugs avoids leaking model-generated identifiers into prose.
  if (!page?.url) return null;

  return <KindLink href={page.url}>{page.title}</KindLink>;
}

/**
 * Kind-tinted link opened in a new tab: green + book for /blog/, purple +
 * file for /pages/, muted otherwise. Used by PageLink and by
 * ChipAwareMarkdown for backend-emitted plain links.
 */
export function KindLink({
  href,
  children,
}: {
  href: string | undefined;
  children: ReactNode;
}): JSX.Element {
  const kind = classifyLinkUrl(href);
  const glyph = LinkKindGlyph({ kind });
  // blog and page links are tracked; generic ones (the CGU…) aren't
  const track = kind === 'generic' ? undefined : `chatbot-texte-${kind}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      content-content={track}
      className={`${track ? 'gtmtrack ' : ''}tw-inline-flex tw-items-baseline tw-gap-1 tw-font-semibold tw-underline tw-decoration-2 tw-underline-offset-2 tw-transition-colors ${LINK_KIND_CLASSES[kind]}`}
    >
      {glyph && (
        <span aria-hidden="true" className="tw-relative tw-top-[2px]">
          {glyph}
        </span>
      )}
      <span>{children}</span>
    </a>
  );
}
